/*
 * Estatísticas de treino para o Progresso: tudo calculado a partir do histórico que o servidor já dá (treinos concluídos), sem inventar nada.
 * Funções puras (recebem "agora"), para se testarem sem relógio.
 */

const DAY = 24 * 60 * 60 * 1000;

/** Segunda-feira, 00:00 (hora local), da semana desta data. */
export function weekStart(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d;
}

const completed = (items) => (items || []).filter((i) => i && i.status === 'Completed' && i.performedAt);

const when = (item) => new Date(/(Z|[+-]\d\d:?\d\d)$/i.test(String(item.performedAt)) ? item.performedAt : `${item.performedAt}Z`);

/** As últimas N semanas (a atual incluída), da mais antiga para a mais recente. */
export function weeklyBuckets(items, now = new Date(), weeks = 8) {
  const thisWeek = weekStart(now).getTime();
  const buckets = Array.from({ length: weeks }, (_, i) => {
    const start = new Date(thisWeek - (weeks - 1 - i) * 7 * DAY);
    return { start, sessions: 0, volumeKg: 0, minutes: 0 };
  });
  for (const item of completed(items)) {
    const idx = Math.floor((weekStart(when(item)).getTime() - (thisWeek - (weeks - 1) * 7 * DAY)) / (7 * DAY) + 0.0001);
    if (idx < 0 || idx >= weeks) continue;
    const b = buckets[idx];
    b.sessions += 1;
    b.volumeKg += Number(item.volumeKg) || 0;
    b.minutes += Math.round((Number(item.durationSeconds) || 0) / 60);
  }
  return buckets;
}

/** Semanas seguidas com pelo menos um treino, a contar da atual (ou da anterior, se esta semana ainda não treinou). */
export function weekStreak(items, now = new Date()) {
  const weeks = new Set(completed(items).map((i) => weekStart(when(i)).getTime()));
  let cursor = weekStart(now).getTime();
  if (!weeks.has(cursor)) cursor -= 7 * DAY;
  let streak = 0;
  while (weeks.has(cursor)) { streak += 1; cursor -= 7 * DAY; }
  return streak;
}

/** Os números dos últimos 30 dias e o total. */
export function summarize(items, now = new Date()) {
  const done = completed(items);
  const since = now.getTime() - 30 * DAY;
  const recent = done.filter((i) => when(i).getTime() >= since);
  const sum = (list, key) => list.reduce((n, i) => n + (Number(i[key]) || 0), 0);
  return {
    total: done.length,
    last30: { sessions: recent.length, volumeKg: Math.round(sum(recent, 'volumeKg')), minutes: Math.round(sum(recent, 'durationSeconds') / 60) },
    streakWeeks: weekStreak(done, now),
    lastAt: done.length ? done.reduce((a, b) => (when(a) > when(b) ? a : b)).performedAt : null,
  };
}

/** Um recorde com a subida desde a primeira vez ("+10 kg"), ou null se só o fez uma vez / não subiu. */
export function recordGain(record) {
  const gain = Number(record?.bestWeightKg) - Number(record?.startWeightKg);
  return Number.isFinite(gain) && gain > 0 ? Math.round(gain * 10) / 10 : null;
}

/**
 * Os números da SEMANA ATUAL (segunda a domingo, hora local — a mesma `weekStart` das barras): treinos concluídos,
 * séries registadas e volume (Σ carga × repetições), a partir dos treinos concluídos. Um treino sem carga não soma
 * volume — não se inventa peso. Tudo sai do histórico real; sem histórico, tudo a zero.
 */
export function weekSummary(items, now = new Date()) {
  const start = weekStart(now).getTime();
  const end = start + 7 * DAY;
  const inWeek = completed(items).filter((i) => {
    const t = when(i).getTime();
    return t >= start && t < end;
  });
  const sum = (key) => inWeek.reduce((n, i) => n + (Number(i[key]) || 0), 0);
  return { sessions: inWeek.length, sets: sum('setsLogged'), volumeKg: Math.round(sum('volumeKg')) };
}

/**
 * Os pontos da evolução de UM exercício, prontos a desenhar: do mais antigo para o mais recente (o sentido de leitura
 * de um gráfico — o servidor entrega ao contrário). Só sessões datadas com dados; sem carga e sem 1RM o ponto traz nulo
 * nesses campos (o gráfico não desenha um zero que não existe). A repetição associada é a melhor série com carga da sessão.
 */
export function progressionPoints(history) {
  const sessions = history?.sessions || [];
  return sessions
    .filter((s) => s && s.performedAt)
    .map((s) => {
      const loaded = (s.sets || []).filter((x) => Number(x.weightKg) > 0 && Number(x.reps) > 0);
      const repMax = loaded.reduce((m, x) => Math.max(m, Number(x.reps)), 0);
      const topWeightKg = Number(s.topWeightKg) > 0 ? Number(s.topWeightKg) : null;
      const oneRepMaxKg = Number(s.estimatedOneRepMaxKg) > 0 ? Number(s.estimatedOneRepMaxKg) : null;
      return { performedAt: s.performedAt, topWeightKg, repMax: repMax || null, oneRepMaxKg };
    })
    .sort((a, b) => when(a).getTime() - when(b).getTime());
}
